import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import {
  Settings,
  SmartToy,
  SettingsSuggest,
  Tune,
  Wallpaper
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// Import sub-components
import GeneralSettings from './settings/GeneralSettings';
import GuruSettings from './settings/GuruSettings';
import SystemPrompts from './settings/SystemPrompts';
import EngineSettings from './settings/EngineSettings';
import DesignSettings from './settings/DesignSettings';

// Import hooks and constants
import { useUserSettings } from '../contexts/UserSettingsContext';

const CustomSettings = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { settings, updateSettings } = useUserSettings();

  // State for UI
  const [selectedSection, setSelectedSection] = useState('general');

  // Handler for saving settings from sub-components
  const handleSettingsUpdate = async (updateData) => {
    await updateSettings({
      ...settings,
      ...updateData
    });
  };

  const settingsSections = [
    { id: 'general', label: t('settings.sections.general'), icon: <Settings /> },
    { id: 'design', label: t('settings.sections.design', { defaultValue: 'Design' }), icon: <Wallpaper /> },
    { id: 'guru', label: t('settings.sections.guru'), icon: <SmartToy /> },
    { id: 'systemPrompts', label: t('settings.sections.systemPrompts'), icon: <SettingsSuggest /> },
    { id: 'engineSettings', label: t('settings.sections.engineSettings'), icon: <Tune /> },
  ];

  const engineOptions = [
    { key: 'chatgpt', text: t('engine.chatgpt') },
    { key: 'claude', text: t('engine.claude') },
    { key: 'gemini', text: t('engine.gemini') },
    { key: 'deepseek', text: t('engine.deepseek') },
    { key: 'grok', text: t('engine.grok') },
  ];

  // Render the appropriate settings section
  const renderSettingsSection = () => {
    switch (selectedSection) {
      case 'general':
        return (
          <GeneralSettings
            initialSettings={settings}
            onSave={handleSettingsUpdate}
          />
        );

      case 'design':
        return (
          <DesignSettings
            initialSettings={settings}
            onSave={handleSettingsUpdate}
          />
        );

      case 'guru':
        return (
          <GuruSettings
            initialSettings={settings}
            onSave={handleSettingsUpdate}
            engineOptions={engineOptions}
          />
        );

      case 'systemPrompts':
        return (
          <SystemPrompts
            initialPrompts={settings.systemPrompts || []}
            onSave={handleSettingsUpdate}
          />
        );

      case 'engineSettings':
        return (
          <EngineSettings
            initialSettings={settings}
            onSave={handleSettingsUpdate}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '90vh',
          maxHeight: '90vh',
          width: isMobile ? '100%' : '90vw',
          overflowY: 'auto'
        }
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: isMobile ? 2 : 3
      }}>
        {t('settings.title')}
      </DialogTitle>
      <DialogContent sx={{
        padding: 0,
        display: 'flex',
        height: isMobile ? 'calc(100vh - 120px)' : '500px',
        overflow: 'hidden'
      }}>
        {/* Left Panel - Settings Navigation */}
        <Box sx={{
          width: isMobile ? '80px' : '200px',
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.action.hover,
          overflow: 'auto'
        }}>
          <List>
            {settingsSections.map((section) => (
              <ListItem
                data-testid={`settings-section-${section.id}`}
                key={section.id}
                button
                selected={selectedSection === section.id}
                onClick={() => setSelectedSection(section.id)}
                sx={{
                  flexDirection: isMobile ? 'column' : 'row',
                  py: isMobile ? 2 : 1,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{
                  minWidth: isMobile ? 'auto' : 40,
                  color: selectedSection === section.id ?
                    theme.palette.primary.contrastText :
                    theme.palette.text.primary
                }}>
                  {section.icon}
                </ListItemIcon>
                {!isMobile && <ListItemText primary={section.label} />}
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Right Panel - Settings Content */}
        <Box sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          p: 2,
          width: 'calc(100% - 200px)'
        }}>
          {renderSettingsSection()}
        </Box>
      </DialogContent>
      <DialogActions sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: isMobile ? 2 : 3
      }}>
        <Button onClick={onClose} color="primary" variant="contained">
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomSettings;